import React, {useEffect, useState} from 'react';
import {useFeedbackTools} from "./components/FeedbackTools";

const PWAContext = React.createContext();

const SW_URL = `${process.env.PUBLIC_URL}/service-worker.js`;

const PWAProvider = props => {
    const {children} = props;
    const {askConfirmation} = useFeedbackTools();
    const [deferredPrompt, setDeferredPrompt] = useState(null);

    useEffect(() => {
        const handleBeforeInstallPrompt = e => {
            e.preventDefault();
            setDeferredPrompt(e);
            console.log(`'beforeinstallprompt' event was fired.`);
        };

        // listen service worker update event
        window.addEventListener("onServiceWorkerUpdate", onWorkerUpdate);

        // listen install event
        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
            window.removeEventListener("onServiceWorkerUpdate", onWorkerUpdate);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // private function triggered when the service worker detect a new version of the software
    const onWorkerUpdate = async () => {
        const serviceWorkerRegistration = await navigator?.serviceWorker?.getRegistration(SW_URL);
        const waitingWorker = serviceWorkerRegistration?.waiting;
        console.log("onWorkerUpdate", waitingWorker);
        if (waitingWorker) {
            askConfirmation({
                title: "Aggiornamento",
                description: "È disponibile una nuova versione dell'applicazione",
                confirmationText: 'Aggiorna ora',
                cancellationText: '---',
                disableClose: true,
                onConfirm: () => {
                    waitingWorker.postMessage({type: 'SKIP_WAITING'});

                    // Listen for the controlling service worker to change and reload the page (added later to
                    // fix crash after the update)
                    waitingWorker.addEventListener('statechange', (event) => {
                        if (event.target.state === 'activated') {
                            window.location.reload();
                        }
                    });
                }
            });
        }
    }

    const checkPWAUpdates = async () => {
        console.log("checkPWAUpdates", 'serviceWorker' in navigator);
        if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.getRegistration(SW_URL);
            console.log("registration", registration, registration?.update);
            registration?.update();
        }
    }

    // For more details https://web.dev/customize-install/
    const onInstall = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            // Wait for the user to respond to the prompt
            const {outcome} = await deferredPrompt.userChoice;
            // We've used the prompt, and can't use it again, throw it away
            setDeferredPrompt(null);

            console.log(`User response to the install prompt: ${outcome}`);
            return outcome;
        }
        return "notInstallable";
    }

    // Check if display mode is standalone (as declared into the manifest).
    // Display mode is standalone could means the user is navigating using the INSTALLED PWA
    // If true we are pretty sure that the user has installed the PWA :-)
    // If false we do not know if the PWA is installed, it can or cannot :-\
    const isStandaloneMode = () => {
        // For iOS (it is a non standard attributed - in 2021 is supported only by ios)
        if (window.navigator.standalone) {
            return true
        }

        // For Android
        if (window.matchMedia('(display-mode: standalone)').matches) {
            return true
        }

        // If neither is true, it's not installed
        return false
    }

    const context = {
        isPWAInstallable: !!deferredPrompt,
        installPWA: onInstall,
        checkPWAUpdates,
        isStandaloneMode
    };

    return (
        <PWAContext.Provider value={context}>
            {children}
        </PWAContext.Provider>
    )
};

const usePWA = () => React.useContext(PWAContext)

export {PWAProvider, usePWA}